// The auth branch (§1.3): 14 routes under /chrome-extension/auth.
//
// Screens come from packages/ui so the extension popup reuses them in Phase 6.2 instead of
// keeping its own AngularJS copies (§1.6). This file holds only what is specific to the SPA:
// routing, loaders, and the redirect decisions.
import { createElement, useEffect, useRef, useState } from 'react'
import {
  Outlet,
  redirect,
  useNavigate,
  useOutletContext,
  useSearchParams,
  type RouteObject,
} from 'react-router-dom'
import {
  ApiError,
  ExtensionUserStatus,
  getMultiFactorQrImage,
  getUser,
  isSsoRequired,
  login,
  logout,
  oAuthSignInUrl,
  register,
  requestMultiFactorAuthReset,
  fetchViewModel,
  requestPasswordReset,
  resetPassword,
  updateUser,
  validateMultiFactorAuth,
  type ExtensionUser,
  type OAuthProvider,
} from '@woo-ext/api'
import {
  EnableCookiesPage,
  ForgotPasswordPage,
  LoginPage,
  MfaResetRequestPage,
  MfaValidatePage,
  OutOfDatePage,
  EXTENSION_STORE_URL,
  ResetPasswordPage,
  SignUpPage,
  SsoCallbackPage,
  StatusIndications,
  StatusIndicationType,
  StatusPage,
  UserPage,
  VerifyPage,
  statusWithInfo,
  type StatusIndication,
} from '@woo-ext/ui'
import { UserAuthStateChangeEvent } from '@woo-ext/protocol'
import { publishAuthStateChange } from '../authBroadcast.ts'
import { extensionTargetId } from '../extension-target.ts'
import { loadRecaptcha } from '../recaptcha.ts'
import type { AuthOutletContext } from './AuthLayout.tsx'

/**
 * Upstream's `$urlRouterProvider.otherwise` (§1.3), preserved decision for decision:
 * resolve the user, then
 *   - resolved            -> /auth/status
 *   - rejected, mfaValidated === false -> /auth/mfa/validate
 *   - rejected otherwise  -> /auth/sign-in
 *
 * ⚠️ `/auth/sign-in` has NO registered state upstream — the login state is `/auth/login` —
 * so that third branch silently re-enters `otherwise` today, which is open question 3. The
 * port routes to `/auth/login`, the screen the user was always meant to reach, and leaves
 * the question of whether to alias `sign-in` upstream where it belongs: upstream.
 */
export async function otherwiseRedirect(): Promise<Response> {
  try {
    await getUser()
    return redirect('/chrome-extension/auth/status')
  } catch (error) {
    if (error instanceof ApiError && error.needsMfa) {
      return redirect('/chrome-extension/auth/mfa/validate')
    }
    return redirect('/chrome-extension/auth/login')
  }
}

function LoginRoute(): React.JSX.Element {
  const navigate = useNavigate()
  return (
    <LoginPage
      checkSsoRequired={isSsoRequired}
      onSignIn={async (email, password) => {
        await login(email, password)
        // ⚠️ BEFORE THE NAVIGATION, which is upstream's order (`login.page.component`
        // awaits `notifyAuthStateChange` and only then follows `rd`). The receivers
        // revalidate against the session this call just established, so publishing after a
        // local navigation races the screen that is already re-reading it.
        publishAuthStateChange(UserAuthStateChangeEvent.SignIn)
        // The MFA filter sits on POST /auth/login (§1.4), so a correct password can still
        // owe a second factor. Re-asking the user endpoint is how upstream learns which.
        await otherwiseRedirectTo(navigate)
      }}
      onSignInOAuth={(provider: OAuthProvider) => {
        // A real window, not fetch: the OAuth round-trip has to set cookies on this origin,
        // and it returns through the tenant's server-side redirect (§1.7 trap 3).
        //
        // A POPUP, matching `login.page.component.ng1.ts:159` verbatim, down to the window
        // name and `noopener`. Navigating this window instead — which the first port did —
        // takes the iframe the extension runs in with it: the provider's consent screen would
        // render inside a 200px panel, and the app would have to boot again afterwards.
        window.open(oAuthSignInUrl(provider), 'oauthPopup', 'popup,noopener')
      }}
    />
  )
}

async function otherwiseRedirectTo(navigate: (to: string) => void): Promise<void> {
  try {
    await getUser()
    navigate('/chrome-extension/auth/status')
  } catch (error) {
    navigate(
      error instanceof ApiError && error.needsMfa
        ? '/chrome-extension/auth/mfa/validate'
        : '/chrome-extension/auth/login',
    )
  }
}

/** Screens Phase 2 does not port. Registered so the route resolves and says what is missing. */
/**
 * `status.page.component.ng1.ts`: resolve the user, then show Connected — or, for an account
 * still Pending, VerificationPending with the address the mail went to. Any rejection is
 * Disconnected. The permission check above it belongs to the shell (Phase 6.1), so the port
 * takes upstream's own default of `permissionsGranted = true` rather than inventing a state.
 */
function StatusRoute(): React.JSX.Element {
  const navigate = useNavigate()
  // ⚠️ NOT a second `getUser()`. The guard fetched this user to decide whether the route may
  // render at all, and re-fetching it made every visit two sequential requests for the same
  // session. Disconnected remains the right face for "no user" — but NOT for "owes a second
  // factor", which the route declares as `requiresNonMfaVerificationPendingAuth` and the
  // guard redirects away before this component ever renders.
  const { user } = useOutletContext<AuthOutletContext>()

  const status: StatusIndication =
    user === undefined
      ? StatusIndications[StatusIndicationType.Disconnected]
      : user.status === ExtensionUserStatus.Pending
        ? statusWithInfo(
            StatusIndicationType.VerificationPending,
            `A verification email was sent to ${user.email ?? ''}, check your mailbox.`,
          )
        : StatusIndications[StatusIndicationType.Connected]

  return (
    <StatusPage
      status={status}
      // The guard does not release the outlet until the session is resolved, so by the time
      // this renders there is nothing left to wait for.
      loading={false}
      onSignOut={async () => {
        await logout()
        publishAuthStateChange(UserAuthStateChangeEvent.SignOut)
        navigate('/chrome-extension/auth/login')
      }}
    />
  )
}

/**
 * ⚠️ UPSTREAM HAS NO ERROR BRANCH HERE, AND THIS PORT CANNOT COPY THAT. In ui-router the
 * state is only reachable with a session — `requiresAuth` data plus the `otherwise` handler
 * do the gating — so a rejected `get()` means the router should never have let you in.
 * React Router has no such guard yet, so `/auth/user` is directly reachable: swallowing the
 * rejection left `user` undefined and the spinner up FOREVER, for a direct visit or an
 * expired session alike.
 *
 * So the rejection is routed the way `otherwise` would have routed it (§1.3): pending MFA to
 * the validate screen, anything else to login. That is the guard, expressed at the one place
 * that currently needs it.
 */
/**
 * ⚠️ `/auth/user` IS UNGUARDED — it declares `privAccess` but none of the three `requires*`
 * flags (`routes.json`) — so it always fetches its own user, and there is deliberately no
 * fast path here.
 *
 * An earlier version took one from the outlet context "when arriving from `/auth/status`",
 * which was DEAD CODE and, worse, a comment claiming an optimisation that cannot happen:
 * the guard only resolves a session for guarded routes, so on this pathname the context
 * carries no user whatever you navigated from. `StatusRoute` does have that fast path and it
 * is real — that route is guarded, and it measurably went from two `/user` calls to one.
 *
 * Making this route guarded to enable the reuse would be the wrong repair: the flags come
 * from upstream's state `data`, and inventing one here to save a request is exactly the kind
 * of divergence the generated route table exists to prevent.
 */
function UserRoute(): React.JSX.Element {
  const navigate = useNavigate()
  const [fetched, setFetched] = useState<ExtensionUser | undefined>(undefined)
  const [edited, setEdited] = useState<ExtensionUser | undefined>(undefined)
  const current = edited ?? fetched

  useEffect(() => {
    let live = true
    void getUser().then(
      (loaded) => {
        if (live) setFetched(loaded)
      },
      (error: unknown) => {
        // No guard ran for this route, so the routing decision upstream's `otherwise` would
        // have made has to happen here (§1.3).
        if (!live) return
        navigate(
          error instanceof ApiError && error.needsMfa
            ? '/chrome-extension/auth/mfa/validate'
            : '/chrome-extension/auth/login',
        )
      },
    )
    return () => {
      live = false
    }
  }, [navigate])

  if (current === undefined) return <i className="far fa-spinner fa-spin woo-loader" />

  return (
    <UserPage
      name={current.name ?? ''}
      email={current.email ?? ''}
      onSave={async (name) => {
        // The whole DTO goes back, as upstream does — a patch drops fields the server keeps.
        setEdited(await updateUser({ ...current, name }))
      }}
    />
  )
}

/**
 * ⚠️ ONLY A 403 MEANS "THERE IS NO QR". The screen offers "Can't find it?" — a link to
 * REQUEST AN MFA RESET — whenever the QR is absent, so turning every failure into absence
 * invites a user to file a reset request because the tenant was down for a moment. A 401 is
 * an expired session and belongs at the login screen; a 500 or a network failure is neither,
 * and the honest thing is to show the form without pretending the reset path is the answer.
 */
function MfaValidateRoute(): React.JSX.Element {
  const navigate = useNavigate()
  const [qrBase64, setQrBase64] = useState<string | undefined>(undefined)
  /** 'enrolled' is upstream's absent-QR case: a 403, and the only one that offers the reset. */
  const [qrOutcome, setQrOutcome] = useState<'loading' | 'enrolled' | 'unavailable'>('loading')

  useEffect(() => {
    let live = true
    void getMultiFactorQrImage().then(
      (qr) => {
        if (!live) return
        setQrBase64(qr)
        setQrOutcome(qr === undefined ? 'enrolled' : 'loading')
      },
      (error: unknown) => {
        if (!live) return
        if (error instanceof ApiError && error.isForbidden) {
          // Absent for an account that finished enrolment — not an error, and the screen
          // keys its "Can't find it?" link off exactly this.
          setQrOutcome('enrolled')
          return
        }
        if (error instanceof ApiError && error.isUnauthenticated) {
          navigate('/chrome-extension/auth/login')
          return
        }
        setQrOutcome('unavailable')
      },
    )
    return () => {
      live = false
    }
  }, [navigate])

  return (
    <MfaValidatePage
      qrBase64={qrBase64}
      // Offered only when the server actually said "you are enrolled", never because a call
      // failed — see the note above.
      showResetLink={qrOutcome === 'enrolled'}
      onVerify={async (code) => {
        await validateMultiFactorAuth(code)
        publishAuthStateChange(UserAuthStateChangeEvent.UserMFAVerified)
        navigate('/chrome-extension/auth/status')
      }}
      onSignOut={async () => {
        await logout()
        publishAuthStateChange(UserAuthStateChangeEvent.SignOut)
        navigate('/chrome-extension/auth/login')
      }}
    />
  )
}

/**
 * `?token` is the reset link's payload. Upstream reads it from `$stateParams`; without one
 * the endpoint rejects, which is the same "Something went wrong" the screen already shows —
 * so there is no separate missing-token state to invent.
 */
function ResetPasswordRoute(): React.JSX.Element {
  const [params] = useSearchParams()
  const token = params.get('token') ?? ''
  // Upstream branches its completion copy on `isLoggedIn` — a signed-in user is told the
  // password was reset, a signed-out one is given a link to the login screen. The first port
  // never passed it, so the signed-in branch was unreachable and a signed-in user was always
  // told to "login here".
  const [signedIn, setSignedIn] = useState(false)
  // ⚠️ RESOLVED THROUGH `extensionTarget`, NOT READ OFF THE BOOTSTRAP. The install link points
  // at a STORE LISTING, and during parallel launch there are two — `viewModel.extensionId` is
  // the LEGACY one (CLAUDE.md 6), so using it directly would send a user who is being onboarded
  // onto the new extension to install the old one.
  const [installUrl, setInstallUrl] = useState<string | undefined>(undefined)
  useEffect(() => {
    let live = true
    void fetchViewModel().then(
      (model) => {
        const id = extensionTargetId(model)
        if (live && id !== undefined) setInstallUrl(`${EXTENSION_STORE_URL}${id}`)
      },
      () => {
        // No bootstrap, no id — the CTA falls back to the bare store URL, which still reaches
        // a searchable listing rather than a dead link.
      },
    )
    void getUser().then(
      () => {
        if (live) setSignedIn(true)
      },
      () => {
        if (live) setSignedIn(false)
      },
    )
    return () => {
      live = false
    }
  }, [])

  return (
    <ResetPasswordPage
      {...(installUrl === undefined ? {} : { installUrl })}
      signedIn={signedIn}
      onReset={async (password) => {
        await resetPassword(token, password)
        // ⚠️ THE PASSWORD IS ALREADY CHANGED BY THE TIME THIS RUNS, and upstream wraps the
        // notification in its own try/catch for exactly that reason: "a failed extension
        // notification (no extension installed / Incognito) must not be reported as a failed
        // reset." A throw here would surface as a reset that appears to have failed and will
        // not work if retried, because the token is spent.
        try {
          publishAuthStateChange(UserAuthStateChangeEvent.PasswordReset)
        } catch {
          // best-effort
        }
      }}
    />
  )
}

/**
 * Sign-up needs a reCAPTCHA token on any tenant that has validation ON, which is every
 * tenant except the dev ones (`captchaValidationDisabled`). The first port sent an empty
 * string and called the flow ported: it worked against the one tenant it was tested on and
 * would have failed everywhere else.
 *
 * The widget is mounted from the bootstrap's `recaptchaSiteKey` — which `/extension/view-model`
 * already serves — and the token is read at submit time. Where the site key is absent, the
 * form still submits with an empty token, which is correct for a tenant that does not
 * validate and an honest failure on one that does.
 */
function SignUpRoute(): React.JSX.Element {
  const navigate = useNavigate()
  const widgetId = useRef<number | undefined>(undefined)

  useEffect(() => {
    let cancelled = false
    void (async () => {
      // The site key is per tenant and arrives with the bootstrap, so the widget cannot be
      // mounted until that answers. A tenant that sends no key (or sets
      // captchaValidationDisabled) simply gets no widget.
      const viewModel = await fetchViewModel()
      const siteKey = typeof viewModel.recaptchaSiteKey === 'string' ? viewModel.recaptchaSiteKey : ''
      // BOTH conditions. A tenant can serve a site key AND disable validation — the dev ones
      // do exactly that — and loading Google's script to render a widget nobody checks is a
      // third-party request made for nothing.
      if (cancelled || siteKey.length === 0 || viewModel.captchaValidationDisabled === true) return

      const grecaptcha = await loadRecaptcha()
      const container = document.querySelector('#g-recaptcha-container')
      if (cancelled || container === null || widgetId.current !== undefined) return
      widgetId.current = grecaptcha.render(container as HTMLElement, { sitekey: siteKey })
    })().catch((error: unknown) => {
      // A blocked or failed third-party script must not take the form down: the submit below
      // then sends an empty token, and a tenant that validates rejects it visibly.
      //
      // ⚠️ BUT IT IS REPORTED, BECAUSE SWALLOWING IT HID A REAL BUG FOR MONTHS. `loadRecaptcha`
      // used to resolve before `grecaptcha.render` existed, so this caught a TypeError on
      // every single mount — and on a tenant that validates, that is a sign-up screen nobody
      // can complete. The catch is here for a blocked CDN, not for our own mistakes.
      console.error('reCAPTCHA did not mount; sign-up will submit an empty token', error)
    })
    return () => {
      cancelled = true
    }
  }, [])

  return (
    <SignUpPage
      checkSsoRequired={isSsoRequired}
      onSignUp={async (name, email, password) => {
        const token = widgetId.current === undefined ? '' : (window.grecaptcha?.getResponse(widgetId.current) ?? '')
        try {
          await register(name, email, password, token)
        } catch (error) {
          // ⚠️ A reCAPTCHA v2 response is SINGLE USE. The failed call consumed it, so every
          // retry re-submits a token the server has already rejected and the user cannot get
          // past the form without reloading the page. `SignUpPage` catches this to show its
          // error, which is why the reset has to happen here, before it does.
          if (widgetId.current !== undefined) window.grecaptcha?.reset(widgetId.current)
          throw error
        }
        publishAuthStateChange(UserAuthStateChangeEvent.SignUp)
        navigate('/chrome-extension/auth/status')
      }}
    />
  )
}

/**
 * The IdP comes back to `/auth/sso/callback` with either a session or an error.
 *
 * ⚠️ THE PARAMETER NAMES ARE PREFIXED, and the first port guessed the unprefixed ones. The
 * contract in `routes.json` is `otype`, `oevent`, `oerror`, `ouser`, `omessage` —
 * `callback.page.component.ng1.ts:42-45` reads exactly those. Reading `?error` means a real
 * IdP failure leaves it undefined, so the page asks the server for a user it does not have
 * and shows a spinner instead of the error.
 *
 * It is also genuinely the popup the login screen opened: upstream closes the window when
 * `window.name === 'oauthPopup'` rather than navigating, because there is a parent tab
 * waiting behind it.
 */
function SsoCallbackRoute(): React.JSX.Element {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const oAuthError = params.get('oerror') ?? undefined
  const oAuthType = params.get('otype') ?? undefined
  const oAuthEvent = params.get('oevent') ?? undefined

  useEffect(() => {
    let live = true
    void getUser().then(
      () => {
        if (!live) return
        // `!isLoggedIn() || !!oerror` is upstream's condition — a session alone is not
        // enough if the IdP also reported a failure.
        if (oAuthError !== undefined) {
          setError(true)
          setLoading(false)
          return
        }
        // ⚠️ BEFORE THE `close()` BELOW, not after it. This window is the one the login
        // screen opened, and closing it first would take the publisher with it — the
        // tab waiting behind would never learn that the OAuth round-trip signed anyone in,
        // which is the whole reason that tab is waiting.
        publishAuthStateChange(
          // `ExtensionOAuthEvent.SignUp`. Anything else — including a missing `oevent` — is
          // a sign-in, which is upstream's own default rather than an assumption here.
          oAuthEvent === 'SignUp'
            ? UserAuthStateChangeEvent.OAuthSignUp
            : UserAuthStateChangeEvent.OAuthSignIn,
        )
        if (window.name === 'oauthPopup') {
          window.close()
          return
        }
        navigate('/chrome-extension/auth/status')
      },
      () => {
        if (!live) return
        setError(true)
        setLoading(false)
      },
    )
    return () => {
      live = false
    }
  }, [navigate, oAuthError, oAuthEvent])

  return (
    <SsoCallbackPage
      loading={loading}
      error={error}
      oAuthError={oAuthError}
      oAuthMessage={params.get('omessage') ?? undefined}
      oAuthUser={params.get('ouser') ?? undefined}
      onTryAgain={() => {
        // `otype` names the provider that started the round-trip. Always retrying Google
        // sends a user whose Microsoft sign-in failed into a different identity provider.
        const provider: OAuthProvider = oAuthType === 'microsoft' ? 'microsoft' : 'google'
        window.location.assign(oAuthSignInUrl(provider))
      }}
    />
  )
}

export const authRoutes: RouteObject[] = [
  { path: 'login', element: <LoginRoute /> },
  {
    path: 'forgot-password',
    element: (
      <ForgotPasswordPage
        onRequestReset={async (email) => {
          await requestPasswordReset(email)
        }}
      />
    ),
  },
  { path: 'enable-cookies', element: <EnableCookiesPage /> },
  {
    path: 'out-of-date',
    element: (
      <OutOfDatePage
        onResolve={() => {
          // Upstream sends `openPopup` to the extension over §1.7's channel 1. There is no
          // shell yet, so this is deliberately inert rather than pretending to work; Phase
          // 6.1 wires it through packages/protocol.
          console.warn('openPopup needs the extension shell (phase 6.1)')
        }}
      />
    ),
  },
  { path: 'verify', element: <VerifyRoute /> },

  // The session-dependent half, ported once a reachable dev tenant made a real session
  // possible (plan §7 phase 2). `mfa` and `sso` are ABSTRACT upstream — they own a URL
  // segment and nothing else — so they are parents here rather than screens.
  { path: 'status', element: <StatusRoute /> },
  { path: 'user', element: <UserRoute /> },
  { path: 'sign-up', element: <SignUpRoute /> },
  { path: 'reset-password', element: <ResetPasswordRoute /> },
  { path: 'mfa/validate', element: <MfaValidateRoute /> },
  { path: 'mfa/reset-request', element: <MfaResetRequestRoute /> },
  {
    // ⚠️ `sso` IS ABSTRACT AND ITS TEMPLATE IS A REAL BOX. Upstream declares
    // `template: '<ui-view class="chrome-extension-auth-sso"></ui-view>'` on the parent state,
    // and that element sits between the auth wrapper and the page — unstyled, so `display:
    // inline` and `height: auto`. The page element inside it is `display: flex` with
    // `align-items: center` under `.non-extended-ui`, and what it centres in is THAT box: 297px
    // tall in production, not the 900 of the frame. Routed flat, the page element stretched to
    // the full height instead and the screen sat 302px lower — every pixel of `sso/callback`'s
    // 5.03%, on a screen whose content is otherwise identical. Same lesson as the auth wrapper
    // above, one level further in.
    path: 'sso',
    element: createElement('ui-view', { class: 'chrome-extension-auth-sso' }, <Outlet />),
    children: [{ path: 'callback', element: <SsoCallbackRoute /> }],
  },
]

/**
 * `verify.page.component.ng1.ts`, which is a decision tree rather than a screen.
 *
 * ⚠️ `?verified` IS COMPARED TO THE STRING `'1'`, not to `true`/`false` — line 42. A link
 * carrying `?verified=true` is therefore NOT verified as far as this screen is concerned,
 * and the first port, which never read the parameter at all, could reach none of these
 * states. The four outcomes:
 *   - `?verified=1`            → VerificationCompleted
 *   - `?verified=<anything else>` → VerificationPending, "Email verification failed…"
 *   - no parameter, user Pending  → VerificationPending with the address it went to
 *   - no parameter, user active   → Connected, "You can close this tab now."
 *   - the user call rejects       → Disconnected
 *
 * Not ported: the `VerificationCompletedWithEmailSignin` variant, which upstream picks when
 * `shortlistCandidateMessageService.messageCandidatesEnabled` — that service is Phase 3.
 */
function VerifyRoute(): React.JSX.Element {
  const [params] = useSearchParams()
  const verifiedParam = params.get('verified')
  const [loading, setLoading] = useState(true)
  const [status, setStatus] = useState<StatusIndication>(
    StatusIndications[StatusIndicationType.Disconnected],
  )

  useEffect(() => {
    let live = true

    if (verifiedParam !== null) {
      // `verify.page.component.ng1.ts:42` — only '1'. A FAILED verification notifies nobody:
      // nothing about the session changed, and telling every surface it did would send them
      // all to re-read a session that is exactly as it was.
      if (verifiedParam === '1') {
        try {
          publishAuthStateChange(UserAuthStateChangeEvent.UserVerified)
        } catch {
          // best-effort — upstream's comment: "the page is also reached in a plain tab
          // without the extension (redirect fallback)".
        }
      }
      setStatus(
        verifiedParam === '1'
          ? StatusIndications[StatusIndicationType.VerificationCompleted]
          : statusWithInfo(
              StatusIndicationType.VerificationPending,
              'Email verification failed, please contact support.',
            ),
      )
      setLoading(false)
      return
    }

    void getUser().then(
      (user) => {
        if (!live) return
        setStatus(
          user.status === ExtensionUserStatus.Pending
            ? statusWithInfo(
                StatusIndicationType.VerificationPending,
                `A verification email was sent to ${user.email ?? ''}, check your mailbox.`,
              )
            : statusWithInfo(StatusIndicationType.Connected, 'You can close this tab now.'),
        )
        setLoading(false)
      },
      () => {
        if (!live) return
        setStatus(StatusIndications[StatusIndicationType.Disconnected])
        setLoading(false)
      },
    )

    return () => {
      live = false
    }
  }, [verifiedParam])

  return <VerifyPage status={status} loading={loading} />
}

function MfaResetRequestRoute(): React.JSX.Element {
  return (
    <MfaResetRequestPage
      onRequestReset={async (email) => {
        await requestMultiFactorAuthReset(email)
      }}
    />
  )
}
